import React, { useState, useEffect, useCallback } from 'react';
import { Image, Upload, Trash2, Eye, Camera, Users, Building2, X, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { hasPageAccess } from '../utils/permissions';

// Slider Image Management Section Component
const SliderSection = ({ 
  sliderImages, 
  handleDeleteSlider,
  error,
  setError,
  navigate
}) => (
  <div className="space-y-6">
    {/* Header with Add Button */}
    <div className="flex items-center justify-end mb-4">
      <button
        onClick={() => navigate('/slider-image/new')}
        className="text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center"
        style={{ backgroundColor: '#10b981' }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
      >
        Add Slider Image
      </button>
    </div>

    {/* Existing Slider Images */}
    {sliderImages && sliderImages.length > 0 ? (
      <div>
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {sliderImages.map((image) => (
            <div key={`slider-image-${image.id}`} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm">
              <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                {/* Image Display */}
                <div className="w-full lg:w-48 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={image.image_url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center" style={{ display: 'none' }}>
                    <Image className="h-8 w-8 text-gray-400" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">{image.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{image.description}</p>
                      {image.order !== undefined && (
                        <p className="text-xs text-gray-500 mt-1">Display Order: {image.order}</p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => navigate(`/slider-image/edit/${image.id}`)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSlider(image.id)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ) : (
      <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
        <Image className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Slider Images</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Get started by adding your first slider image for the homepage carousel.
        </p>
      </div>
    )}

    {/* Error Message */}
    {error && (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto pl-3"
          >
            <X className="h-5 w-5 text-red-400 hover:text-red-600" />
          </button>
        </div>
      </div>
    )}
  </div>
);

// Team Member Management Section Component
const TeamMemberSection = ({ 
  teamMembers, 
  handleDeleteTeamMember,
  error,
  setError,
  navigate
}) => (
  <div className="space-y-6">
    {/* Header with Add Button */}
    <div className="flex items-center justify-end mb-4">
      <button
        onClick={() => navigate('/team-member/new')}
        className="text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center"
        style={{ backgroundColor: '#10b981' }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
      >
        Add Team Member
      </button>
    </div>

    {/* Existing Team Members */}
    {teamMembers && teamMembers.length > 0 ? (
      <div>
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {teamMembers.map((member) => (
            <div key={`team-member-${member.id}`} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                {/* Photo Display */}
                <div className="w-full sm:w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={member.photo_url}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center" style={{ display: 'none' }}>
                    <Users className="h-8 w-8 text-gray-400" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900">{member.name}</h4>
                      <p className="text-sm font-medium text-gray-600">{member.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{member.credentials}</p>
                      <p className="text-sm text-gray-700 mt-2 line-clamp-2">{member.bio}</p>
                      {member.order !== undefined && (
                        <p className="text-xs text-gray-500 mt-1">Display Order: {member.order}</p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => navigate(`/team-member/edit/${member.id}`)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTeamMember(member.id)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ) : (
      <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
        <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Team Members</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Get started by adding your first team member with their photo and bio.
        </p>
      </div>
    )}

    {/* Error Message */}
    {error && (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto pl-3"
          >
            <X className="h-5 w-5 text-red-400 hover:text-red-600" />
          </button>
        </div>
      </div>
    )}
  </div>
);

// Facility Photo Management Section Component
const FacilitySection = ({ 
  facilityPhotos, 
  handleDeleteFacilityPhoto,
  error,
  setError,
  navigate
}) => (
  <div className="space-y-6">
    {/* Header with Add Button */}
    <div className="flex items-center justify-end mb-4">
      <button
        onClick={() => navigate('/facility-photo/new')}
        className="text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center"
        style={{ backgroundColor: '#10b981' }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
      >
        Add Facility Photo
      </button>
    </div>

    {/* Existing Facility Photos */}
    {facilityPhotos && facilityPhotos.length > 0 ? (
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {facilityPhotos.map((photo) => (
            <div key={`facility-photo-${photo.id}`} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              {/* Photo Display */}
              <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden mb-4">
                <img
                  src={photo.photo_url}
                  alt={photo.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-gray-100 flex items-center justify-center" style={{ display: 'none' }}>
                  <Building2 className="h-8 w-8 text-gray-400" />
                </div>
              </div>

              {/* Content */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-900">{photo.title}</h4>
                {photo.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{photo.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                    {photo.category}
                  </span>
                  {photo.order !== undefined && (
                    <span className="text-xs text-gray-500">Order: {photo.order}</span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 pt-3 mt-3 border-t border-gray-100">
                <button
                  onClick={() => navigate(`/facility-photo/edit/${photo.id}`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteFacilityPhoto(photo.id)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    ) : (
      <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
        <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Facility Photos</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Get started by adding photos of your facility, equipment, and treatment rooms.
        </p>
      </div>
    )}

    {/* Error Message */}
    {error && (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto pl-3"
          >
            <X className="h-5 w-5 text-red-400 hover:text-red-600" />
          </button>
        </div>
      </div>
    )}
  </div>
);

const PhotoManagement = () => {
  const { user, token, loading: authLoading, API_BASE_URL } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Component states
  const [activeTab, setActiveTab] = useState('homepageslider');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', id: '', name: '' });
  
  // Data states
  const [sliderImages, setSliderImages] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [facilityPhotos, setFacilityPhotos] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Tab configuration
  const tabs = [
    { id: 'homepageslider', label: 'Homepage Slider', icon: Camera },
    { id: 'team', label: 'Team Members', icon: Users },
    { id: 'facility', label: 'Facility Photos', icon: Building2 }
  ];

  // Handle tab parameter from URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['homepageslider', 'team', 'facility'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Fetch data on component mount
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (token && !user) {
      return;
    }

    if (!token || !user || !['admin', 'owner', 'manager'].includes(user.role)) {
      return;
    }

    fetchData();
  }, [token, user, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSliderImages(),
        fetchTeamMembers(),
        fetchFacilityPhotos(),
        fetchUploadedFiles()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadedFiles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/uploaded-files`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUploadedFiles(data);
      }
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
    }
  };

  const fetchSliderImages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/slider-images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSliderImages(data.slider_images || []);
      }
    } catch (error) {
      console.error('Error fetching slider images:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/team-members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.team_members || []);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchFacilityPhotos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/facility-photos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFacilityPhotos(data.facility_photos || []);
      }
    } catch (error) {
      console.error('Error fetching facility photos:', error);
    }
  };

  // Delete functions
  const confirmDeleteSlider = (id) => {
    const slider = sliderImages.find(s => s.id === id);
    setDeleteConfirm({
      show: true,
      type: 'slider',
      id: id,
      name: slider?.title || 'this slider image'
    });
  };

  const confirmDeleteTeamMember = (id) => {
    const member = teamMembers.find(m => m.id === id);
    setDeleteConfirm({
      show: true,
      type: 'team',
      id: id,
      name: member?.name || 'this team member'
    });
  };

  const confirmDeleteFacilityPhoto = (id) => {
    const photo = facilityPhotos.find(p => p.id === id);
    setDeleteConfirm({
      show: true,
      type: 'facility',
      id: id,
      name: photo?.title || 'this facility photo'
    });
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteConfirm;
    
    try {
      const endpoints = {
        slider: 'slider-images',
        team: 'team-members',
        facility: 'facility-photos'
      };

      const response = await fetch(`${API_BASE_URL}/api/${endpoints[type]}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Update local state
        if (type === 'slider') {
          setSliderImages(prev => prev.filter(item => item.id !== id));
        } else if (type === 'team') {
          setTeamMembers(prev => prev.filter(item => item.id !== id));
        } else if (type === 'facility') {
          setFacilityPhotos(prev => prev.filter(item => item.id !== id));
        }
        
        setSuccess(`${type === 'slider' ? 'Slider image' : type === 'team' ? 'Team member' : 'Facility photo'} deleted successfully`);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      setError(`Failed to delete ${type}`);
    } finally {
      setDeleteConfirm({ show: false, type: '', id: '', name: '' });
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue"></div>
      </div>
    );
  }

  // Show access denied for non-authorized users
  // Check if user has permission to access photo management
  if (!hasPageAccess(user, 'website.photos', ['owner', 'manager'])) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access photo management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Manage Photos
              </h2>
            </div>
          </div>

          {/* Content Area */}
          <div className="px-6 py-6">
            {/* Success Alert */}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
                {success}
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            {/* Tab Navigation */}
            <div className="mb-8">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 overflow-x-auto">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          activeTab === tab.id
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-5 w-5 mr-2" />
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            <div>
              {tabs.map((tab) => (
                <div key={tab.id} className={activeTab === tab.id ? 'block' : 'hidden'}>
                  {tab.id === 'homepageslider' ? (
                    <SliderSection 
                      sliderImages={sliderImages}
                      handleDeleteSlider={confirmDeleteSlider}
                      error={error}
                      setError={setError}
                      navigate={navigate}
                    />
                  ) : tab.id === 'team' ? (
                    <TeamMemberSection 
                      teamMembers={teamMembers}
                      handleDeleteTeamMember={confirmDeleteTeamMember}
                      error={error}
                      setError={setError}
                      navigate={navigate}
                    />
                  ) : (
                    <FacilitySection 
                      facilityPhotos={facilityPhotos}
                      handleDeleteFacilityPhoto={confirmDeleteFacilityPhoto}
                      error={error}
                      setError={setError}
                      navigate={navigate}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Confirm Delete
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.
              </p>
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={() => setDeleteConfirm({ show: false, type: '', id: '', name: '' })}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoManagement;